import { Card, CardContent } from "@/components/ui/card";
import { BuddyRead } from "@/types/buddyReads";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Users, Calendar } from "lucide-react";
import { format } from "date-fns";

interface BuddyReadCardProps {
  buddyRead: BuddyRead;
}

export function BuddyReadCard({ buddyRead }: BuddyReadCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const myMember = buddyRead.members?.find(m => m.user_id === user?.id);
  const totalPages = buddyRead.book?.page_count || 0;
  const myProgress = totalPages > 0 ? (myMember?.current_page || 0) / totalPages * 100 : 0;

  // Calculate average progress
  const avgProgress = buddyRead.members && buddyRead.members.length > 0
    ? buddyRead.members.reduce((sum, m) => sum + (m.current_page || 0), 0) / buddyRead.members.length / totalPages * 100
    : 0;

  return (
    <Card
      className="card-elevated cursor-pointer group"
      onClick={() => navigate(`/buddy-read/${buddyRead.id}`)}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Book Cover */}
          <img
            src={buddyRead.book?.cover_url || '/placeholder.svg'}
            alt={buddyRead.book?.title}
            className="w-20 h-28 object-cover rounded shadow-sm"
          />

          {/* Content */}
          <div className="flex-1 space-y-3">
            {/* Title and Book Info */}
            <div>
              {buddyRead.title && (
                <h3 className="font-semibold text-lg">{buddyRead.title}</h3>
              )}
              <p className="font-medium text-sm">
                {buddyRead.book?.title}
              </p>
              <p className="text-xs text-muted-foreground">
                by {buddyRead.book?.authors?.join(', ')}
              </p>
            </div>

            {/* Members */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div className="flex -space-x-2">
                {buddyRead.members?.slice(0, 4).map((member) => (
                  <Avatar key={member.id} className="h-6 w-6 border-2 border-background">
                    <AvatarImage src={member.user?.image} />
                    <AvatarFallback className="text-xs">
                      {member.user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                {buddyRead.members?.length} reader{buddyRead.members?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Progress */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Your progress</span>
                <span className="font-medium">
                  {myMember?.current_page || 0} / {totalPages} pages
                </span>
              </div>
              <Progress value={myProgress} className="h-2" />
            </div>

            {/* Target End Date */}
            {buddyRead.target_end_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>
                  Target: {format(new Date(buddyRead.target_end_date), 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
